<script setup lang="ts">
import { computed, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import WidgetModalShell from '@/components/Dashboard/Modals/WidgetModalShell.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IWidget, WidgetSourceType } from '@/interfaces/components/dashboard/widget'
import type { IWidgetFormData } from '@/interfaces/components/dashboard/widget'
import { gaugeWidgetSchema } from '@/schemas/WidgetModalSchema'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import WidgetModalDataFieldSection from '@/components/Dashboard/Modals/WidgetModalDataFieldSection.vue'
import { useWidgetModalSensorDetails } from '@/composables/useWidgetModalSensorDetails'
import { useGroupStore } from '@/store/group'
import {
  GROUP_INDICATOR_FIELDS,
  getWidgetGroupId,
  getWidgetSensorId,
  getWidgetSourceType,
} from '@/utils/dashboard/widget-source'
import { useI18n } from '@/composable/useI18n'

type ThresholdOperator = 'greater' | 'lower'

const THRESHOLD_OPERATORS: ThresholdOperator[] = ['greater', 'lower']

const props = withDefaults(
  defineProps<{
    sensors: ISensor[]
    initialData?: IWidget
    loading?: boolean
  }>(),
  { initialData: undefined, loading: false },
)

const emit = defineEmits<{
  save: [formData: IWidgetFormData]
  cancel: []
}>()

const { t } = useI18n()
const groupStore = useGroupStore()
const { listGroups } = storeToRefs(groupStore)

const {
  selectedSensor,
  sensorDetails,
  isLoadingDetails,
  detailsError,
  unsupportedSensorType,
  onSensorSelected,
  syncSelectedSensorFromEdit,
} = useWidgetModalSensorDetails()

const fieldLabelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body'
const fieldInputClass =
  'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary'
const fieldErrorClass = 'mt-1 text-sm text-red'

const modalTitle = computed(() =>
  props.initialData
    ? t('custom_dashboards.widget_modal.gauge.title_edit')
    : t('custom_dashboards.widget_modal.gauge.title_create'),
)

const selectedDataField = computed(() => props.initialData?.config.data_field ?? '')

const groupFieldOptions = computed(() =>
  GROUP_INDICATOR_FIELDS.map((value) => ({
    value,
    label: t(`custom_dashboards.widget_modal.group_fields.${value}`),
  })),
)

const initialValues = computed(() => {
  if (props.initialData) {
    const sourceType = getWidgetSourceType(props.initialData)
    return {
      title: props.initialData.title,
      sourceType,
      sensorId: getWidgetSensorId(props.initialData) || '',
      groupId: getWidgetGroupId(props.initialData) || '',
      dataField: props.initialData.config.data_field || '',
      min: props.initialData.config.min ?? 0,
      max: props.initialData.config.max ?? 100,
      threshold: props.initialData.config.threshold ?? 70,
      thresholdOperator: props.initialData.config.threshold_operator || 'greater',
    }
  }

  return {
    title: t('custom_dashboards.widget_modal.gauge.default_title'),
    sourceType: 'sensor' as WidgetSourceType,
    sensorId: '',
    groupId: '',
    dataField: '',
    min: 0,
    max: 100,
    threshold: 70,
    thresholdOperator: 'greater' as ThresholdOperator,
  }
})

watch(
  () => [props.initialData, props.sensors] as const,
  () => {
    void groupStore.fetch()

    if (!props.initialData) {
      void syncSelectedSensorFromEdit(null)
      return
    }

    const sourceType = getWidgetSourceType(props.initialData)
    if (sourceType === 'group') {
      void syncSelectedSensorFromEdit(null)
      return
    }

    const sensorId = getWidgetSensorId(props.initialData) || ''
    const sensor = props.sensors.find((s) => String(s.id) === sensorId) || null
    void syncSelectedSensorFromEdit(sensor)
  },
  { immediate: true },
)

const thresholdOperatorOptions = computed(() =>
  THRESHOLD_OPERATORS.map((value) => ({
    value,
    label: t(`custom_dashboards.widget_modal.gauge.threshold_operators.${value}`),
  })),
)

function thresholdOperatorDescription(operator: ThresholdOperator): string {
  return t(`custom_dashboards.widget_modal.gauge.threshold_operator_descriptions.${operator}`)
}

function onSourceTypeChange(
  sourceType: WidgetSourceType,
  setFieldValue: (field: string, value: unknown) => void,
) {
  setFieldValue('sourceType', sourceType)
  setFieldValue('dataField', '')
  setFieldValue('sensorId', '')
  setFieldValue('groupId', '')
  void syncSelectedSensorFromEdit(null)
}

function onSubmit(values: {
  title: string
  sourceType: WidgetSourceType
  sensorId?: string
  groupId?: string
  dataField: string
  min: number
  max: number
  threshold: number
  thresholdOperator: ThresholdOperator
}) {
  emit('save', {
    title: values.title,
    sourceType: values.sourceType,
    sensorId: values.sourceType === 'sensor' ? values.sensorId : undefined,
    groupId: values.sourceType === 'group' ? values.groupId : undefined,
    dataField: values.dataField,
    min: values.min,
    max: values.max,
    threshold: values.threshold,
    thresholdOperator: values.thresholdOperator,
  })
}
</script>

<template>
  <WidgetModalShell :loading="loading" @cancel="emit('cancel')">
    <template #title>
      <h2 class="font-satoshi mb-6 text-xl font-bold text-black">
        {{ modalTitle }}
      </h2>
    </template>

    <Form
          :validation-schema="gaugeWidgetSchema"
          :initial-values="initialValues"
          @submit="onSubmit"
          v-slot="{ values, setFieldValue, errors }"
        >
          <div class="space-y-5">
            <Field name="title" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.gauge.fields.title') }}
                </label>
                <input
                  v-bind="field"
                  type="text"
                  :class="fieldInputClass"
                  :placeholder="t('custom_dashboards.widget_modal.gauge.placeholders.title')"
                />
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <Field name="sourceType" v-slot="{ field }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.common.fields.source_type') }}
                </label>
                <div class="flex gap-4">
                  <label class="flex cursor-pointer items-center gap-2 text-sm text-black">
                    <input
                      type="radio"
                      :checked="field.value === 'sensor'"
                      class="border-stroke text-blue focus:ring-blue"
                      @change="onSourceTypeChange('sensor', setFieldValue)"
                    />
                    {{ t('custom_dashboards.widget_modal.common.source_types.sensor') }}
                  </label>
                  <label class="flex cursor-pointer items-center gap-2 text-sm text-black">
                    <input
                      type="radio"
                      :checked="field.value === 'group'"
                      class="border-stroke text-blue focus:ring-blue"
                      @change="onSourceTypeChange('group', setFieldValue)"
                    />
                    {{ t('custom_dashboards.widget_modal.common.source_types.group') }}
                  </label>
                </div>
              </div>
            </Field>

            <div v-if="values.sourceType !== 'group'">
              <label :class="fieldLabelClass">
                {{ t('custom_dashboards.widget_modal.gauge.fields.sensor') }}
              </label>
              <DropdownSensors
                name="sensorId"
                :options="sensors"
                :already-selected-option="selectedSensor || undefined"
                :constrain-to-parent="true"
                @option-selected="(sensor: ISensor) => onSensorSelected(sensor, setFieldValue)"
              />
              <p v-if="errors.sensorId" :class="fieldErrorClass">{{ errors.sensorId }}</p>
            </div>

            <Field v-else name="groupId" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.common.fields.group') }}
                </label>
                <div class="relative">
                  <select
                    v-bind="field"
                    :class="[fieldInputClass, 'appearance-none pr-10']"
                  >
                    <option value="">
                      {{ t('custom_dashboards.widget_modal.common.placeholders.group') }}
                    </option>
                    <option
                      v-for="group in listGroups"
                      :key="group.id"
                      :value="String(group.id)"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                </div>
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <WidgetModalDataFieldSection
              v-if="values.sourceType !== 'group'"
              :has-selected-sensor="!!selectedSensor"
              :sensor-details="sensorDetails"
              :is-loading-details="isLoadingDetails"
              :details-error="detailsError"
              :unsupported-sensor-type="unsupportedSensorType"
              :already-selected-data-field="selectedDataField || undefined"
              :field-label-class="fieldLabelClass"
              :field-input-class="fieldInputClass"
              :field-error-class="fieldErrorClass"
            />

            <Field v-else name="dataField" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.common.fields.group_metric') }}
                </label>
                <div class="relative">
                  <select
                    v-bind="field"
                    :class="[fieldInputClass, 'appearance-none pr-10']"
                  >
                    <option value="">
                      {{ t('custom_dashboards.widget_modal.common.placeholders.group_metric') }}
                    </option>
                    <option
                      v-for="option in groupFieldOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                </div>
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <div class="space-y-5 border-t border-stroke pt-5">
              <h3 :class="fieldLabelClass">
                {{ t('custom_dashboards.widget_modal.gauge.sections.config') }}
              </h3>

              <div class="grid grid-cols-3 gap-4">
                <Field name="min" v-slot="{ field, errorMessage }">
                  <div>
                    <label :class="fieldLabelClass">
                      {{ t('custom_dashboards.widget_modal.gauge.fields.min') }}
                    </label>
                    <input v-bind="field" type="number" :class="fieldInputClass" />
                    <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
                  </div>
                </Field>

                <Field name="threshold" v-slot="{ field, errorMessage }">
                  <div>
                    <label :class="fieldLabelClass">
                      {{ t('custom_dashboards.widget_modal.gauge.fields.threshold') }}
                    </label>
                    <input v-bind="field" type="number" :class="fieldInputClass" />
                    <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
                  </div>
                </Field>

                <Field name="max" v-slot="{ field, errorMessage }">
                  <div>
                    <label :class="fieldLabelClass">
                      {{ t('custom_dashboards.widget_modal.gauge.fields.max') }}
                    </label>
                    <input v-bind="field" type="number" :class="fieldInputClass" />
                    <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
                  </div>
                </Field>
              </div>

              <Field name="thresholdOperator" v-slot="{ field, errorMessage }">
                <div>
                  <label :class="fieldLabelClass">
                    {{ t('custom_dashboards.widget_modal.gauge.fields.threshold_operator') }}
                  </label>
                  <div class="relative">
                    <select
                      v-bind="field"
                      :class="[fieldInputClass, 'appearance-none pr-10 uppercase']"
                    >
                      <option
                        v-for="option in thresholdOperatorOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <ChevronDownIcon
                      class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    />
                  </div>
                  <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
                </div>
              </Field>
            </div>

            <div
              v-if="values.thresholdOperator"
              class="border-l-4 border-blue bg-blue-50 px-4 py-3"
            >
              <p class="text-sm leading-relaxed text-body">
                {{ thresholdOperatorDescription(values.thresholdOperator as ThresholdOperator) }}
              </p>
            </div>
          </div>

          <div class="mt-6 border-t border-stroke pt-6">
            <div class="flex justify-end gap-4">
              <button
                type="button"
                class="text-xs font-medium uppercase tracking-wide text-body hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="loading"
                @click="emit('cancel')"
              >
                {{ t('custom_dashboards.widget_modal.gauge.actions.cancel') }}
              </button>
              <button
                type="submit"
                class="flex h-10 min-w-[7rem] items-center justify-center bg-blue px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="loading"
              >
                <ButtonLoader v-if="loading" />
                <span v-else>
                  {{
                    initialData
                      ? t('custom_dashboards.widget_modal.gauge.actions.save')
                      : t('custom_dashboards.widget_modal.gauge.actions.create')
                  }}
                </span>
              </button>
            </div>
          </div>
        </Form>
  </WidgetModalShell>
</template>
